import { Button, Card, Divider, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { type PaymentReconciliationParameters, type PaymentReconciliationReportFormat } from '@parkchirp/reporting';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { AccountFilter, type AccountFilterValue } from './AccountFilter';
import { FacilityHierarchyFilter, type FacilityHierarchyFilterValue } from './FacilityHierarchyFilter';
import { applyFacilityHierarchyToParameters } from './facilityHierarchyParameters';
import { reportingPanelStyles } from './styles';
import type { ReportFormProps } from './types';

type PaymentReconciliationDateRangeValue = {
	startDate: Dayjs | null;
	endDate: Dayjs | null;
};

const initialDateRange: PaymentReconciliationDateRangeValue = {
	startDate: null,
	endDate: null,
};

const initialFacilityHierarchy: FacilityHierarchyFilterValue = {
	areas: [],
	branches: [],
	districts: [],
	facilities: [],
};

export const PaymentReconciliationReportForm = ({
	createReport,
	isGeneratingReport,
	organizationId,
	onValidationError,
}: ReportFormProps) => {
	const [dateRange, setDateRange] = useState<PaymentReconciliationDateRangeValue>(initialDateRange);
	const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: 'all' });
	const [facilityHierarchy, setFacilityHierarchy] = useState<FacilityHierarchyFilterValue>(initialFacilityHierarchy);
	const [format, setFormat] = useState<PaymentReconciliationReportFormat>('csv');

	const reset = () => {
		setDateRange(initialDateRange);
		setAccountFilter({ type: 'all' });
		setFacilityHierarchy(initialFacilityHierarchy);
		setFormat('csv');
	};

	const buildAccountFilter = (): PaymentReconciliationParameters['accountFilter'] => {
		switch (accountFilter.type) {
			case 'accountId':
				return {
					type: 'accountId',
					value: Number(accountFilter.value),
				};

			case 'subscriptionName':
				return {
					type: 'subscriptionName',
					value: accountFilter.value.trim(),
				};

			default:
				return undefined;
		}
	};

	const validate = (): boolean => {
		if (!dateRange.startDate) {
			onValidationError('Select a start date.');
			return false;
		}

		if (dateRange.endDate && dateRange.endDate.isBefore(dateRange.startDate, 'day')) {
			onValidationError('End date must be on or after the start date.');
			return false;
		}

		if (accountFilter.type === 'accountId' && !accountFilter.value) {
			onValidationError('Enter an account ID.');
			return false;
		}

		if (accountFilter.type === 'subscriptionName' && !accountFilter.value.trim()) {
			onValidationError('Enter a subscription name.');
			return false;
		}

		return true;
	};

	const submit = async () => {
		if (!validate() || !dateRange.startDate) {
			return;
		}

		const parameters: PaymentReconciliationParameters = {
			startDate: dateRange.startDate.format('YYYY-MM-DD'),
			format,
		};

		if (dateRange.endDate) {
			parameters.endDate = dateRange.endDate.format('YYYY-MM-DD');
		}

		const account = buildAccountFilter();
		if (account) {
			parameters.accountFilter = account;
		}

		applyFacilityHierarchyToParameters(parameters, facilityHierarchy);

		if (facilityHierarchy.facilities.length === 1) {
			const facilityNumber = facilityHierarchy.facilities[0].facilityNumber;
			if (facilityNumber !== undefined && facilityNumber !== null && `${facilityNumber}` !== '') {
				parameters.facilityNumber = facilityNumber;
			}
		}

		await createReport({
			reportType: 'paymentReconciliation',
			parameters,
		});
	};

	return (
		<Card
			variant='outlined'
			sx={(theme) => ({
				...reportingPanelStyles(theme),
				overflow: 'hidden',
				p: 3,
			})}
		>
			<Stack spacing={2.5}>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack spacing={2}>
						<Typography variant='subtitle1' fontWeight={600}>
							Transaction date range
						</Typography>

						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
							<DatePicker<Dayjs>
								label='Start date'
								value={dateRange.startDate}
								onChange={(startDate) => {
									setDateRange({
										startDate,
										endDate: dateRange.endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>

							<DatePicker<Dayjs>
								label='End date'
								value={dateRange.endDate}
								onChange={(endDate) => {
									setDateRange({
										startDate: dateRange.startDate,
										endDate,
									});
								}}
								slotProps={{ textField: { fullWidth: true } }}
							/>
						</Stack>
					</Stack>
				</LocalizationProvider>

				<Divider />

				<AccountFilter filter={accountFilter} onChange={setAccountFilter} />

				<Divider />

				<FacilityHierarchyFilter
					organizationId={organizationId}
					value={facilityHierarchy}
					onChange={setFacilityHierarchy}
				/>

				<Divider />

				<Stack spacing={1.5}>
					<Typography variant='subtitle1' fontWeight={600}>
						Output
					</Typography>

					<ToggleButtonGroup
						exclusive
						size='small'
						value={format}
						onChange={(_, nextFormat: PaymentReconciliationReportFormat | null) => {
							if (nextFormat) {
								setFormat(nextFormat);
							}
						}}
					>
						<ToggleButton value='csv'>CSV</ToggleButton>
						<ToggleButton value='pdf'>PDF</ToggleButton>
						<ToggleButton value='xlsx'>Excel</ToggleButton>
					</ToggleButtonGroup>
				</Stack>

				<Divider />

				<Stack
					direction='row'
					justifyContent='flex-end'
					spacing={2}
					sx={{
						borderColor: 'divider',
						borderTop: 1,
						pt: 2,
					}}
				>
					<Button onClick={reset} disabled={isGeneratingReport}>
						Reset
					</Button>

					<Button onClick={submit} disabled={isGeneratingReport}>
						Generate Report
					</Button>
				</Stack>
			</Stack>
		</Card>
	);
};
